import { Calendar } from 'primereact/calendar';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import { ColumnEditorOptions } from 'primereact/column';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputNumber, InputNumberValueChangeEvent } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { CSSProperties } from 'styled-components';
import { Option } from 'types/types';

type CellNumberEditorConfig = {
  maxFractionDigits?: number;
  min?: number;
};

export const cellNumberEditor = (options: ColumnEditorOptions, config?: CellNumberEditorConfig) => {
  const handlePasteCapture =
    config?.min !== undefined && config.min >= 0
      ? (e: React.ClipboardEvent) => {
          const text = e.clipboardData.getData('text');
          if (text.includes('-')) {
            e.preventDefault();
            const sanitized = text.replace(/-/g, '');
            const inputEl = (e.currentTarget as HTMLElement).querySelector('input');
            if (inputEl) {
              inputEl.value = sanitized;
              inputEl.dispatchEvent(new Event('input', { bubbles: true }));
            }
          }
        }
      : undefined;

  return (
    <div style={{ display: 'contents' }} onPasteCapture={handlePasteCapture}>
      <InputNumber
        style={{ minWidth: '3rem', height: '32px', border: 0, width: '100%' }}
        inputStyle={{ minWidth: '3rem', width: '100%', height: '32px' }}
        value={options.value}
        onValueChange={(e: InputNumberValueChangeEvent) => options.editorCallback?.(e.value)}
        mode="decimal"
        step={0.01}
        maxFractionDigits={2}
        {...config}
      />
    </div>
  );
};

export const cellDecimalEditor = (options: ColumnEditorOptions, maxLength?: number) => {
  return (
    <InputText
      style={{ minWidth: '3rem', height: '28px', border: 0, width: '98%' }}
      value={options.value || ''}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        const sanitizedValue = replaceInappropriateCharacters(e.target.value);
        options.editorCallback?.(sanitizedValue);
      }}
      maxLength={maxLength}
    />
  );
};

function replaceInappropriateCharacters(value: string): string {
  // U+2010 - HYPHEN
  // U+2011 - NON-BREAKING HYPHEN
  // U+2013 - EN DASH
  // U+2014 - EM DASH
  // U+2212 - MINUS SIGN
  const weirdDashes = /[\u2010\u2011\u2013\u2014\u2212]/g;
  const result = value.replaceAll(weirdDashes, '-').replace(',', '.');
  if (result.toLowerCase() === 'nd') return 'ND';
  return result;
}

export const cellTextEditor = (options: ColumnEditorOptions, placeholder?: string, maxLength?: number) => {
  return (
    <InputText
      style={{ width: '100%', height: '32px', pointerEvents: 'none', border: 0 }}
      placeholder={placeholder}
      type="text"
      value={options.value || ''}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => options.editorCallback?.(e.target.value)}
      maxLength={maxLength}
    />
  );
};

type CellSelectEditorProps = {
  options: ColumnEditorOptions;
  selectOptions: Option[];
  onValueChange?: (params: { field: string; newValue: string; rowData: any }) => void;
  style?: CSSProperties;
  itemTemplate?: any;
  placeholder?: string;
};

export const cellSelectEditor = ({
  options,
  selectOptions,
  onValueChange,
  style,
  itemTemplate,
  placeholder,
}: CellSelectEditorProps) => {
  const { field, rowData, editorCallback } = options;

  return (
    <Dropdown
      style={{ width: '100%', height: '28px', minWidth: '1rem', ...style }}
      value={options.rowData[options.field]}
      optionLabel="label"
      optionValue="value"
      options={selectOptions}
      onChange={(e: DropdownChangeEvent) => {
        editorCallback?.(e.value);
        onValueChange?.({ field, newValue: e.value, rowData });
      }}
      itemTemplate={itemTemplate}
      placeholder={placeholder}
    />
  );
};

export const cellCheckboxEditor = (options: ColumnEditorOptions) => {
  return (
    <Checkbox
      style={{
        minWidth: '1rem',
      }}
      value={options.value}
      checked={options.value}
      onChange={(e: CheckboxChangeEvent) => {
        options.editorCallback?.(e.checked);
      }}
    />
  );
};

export const cellDateRangeEditor = (options: ColumnEditorOptions) => {
  return (
    <Calendar
      style={{ minWidth: '3rem', height: '32px', border: 0, width: '100%' }}
      inputStyle={{ minWidth: '3rem', width: '100%', height: '32px' }}
      value={options.value}
      onChange={(e: any) => options.editorCallback?.(e.value)}
      selectionMode="range"
      readOnlyInput
    />
  );
};
